/**
 * Challenges (lane ALGOBOO2, CHALLENGE1): put two to eight agents side by side over the next week with the same play
 * money, and see the ones you made or have an agent in. The week always starts at the coming midnight (UTC): nobody
 * picks a winner after the fact. `?agent=` pre-fills one agent from its page.
 */
import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { Button, Card, EmptyState, Field, Input, Pill, Skeleton, UiLink, fullTime } from '@algoboo/ui'
import { isApiError, myChallengesQuery, request, useMyChallenges } from '../lib/api'
import { productKeys } from '../lib/api/hooks/product'
import { usePageMeta } from '../lib/meta'
import { PageHeader } from '../components/Shell'
import { SignInPrompt, useSession } from '../components/session'
import { QueryError } from '../components/widgets'
import { prefetchSignedIn } from '../lib/route-data'
import { attempt } from '../lib/attempt'

export const Route = createFileRoute('/challenges/')({
  validateSearch: z.object({ agent: z.string().optional().catch(undefined) }),
  loader: ({ context: { queryClient: qc } }) => prefetchSignedIn(qc, () => [qc.ensureQueryData(myChallengesQuery())]),
  component: ChallengesPage,
})

export const PHASE_WORDS = { upcoming: 'starts soon', running: 'this week', finished: 'finished' } as const

function ChallengesPage() {
  usePageMeta('Challenges')
  const { me, loading } = useSession()
  const { agent } = Route.useSearch()
  if (loading) return <Skeleton className="h-64 rounded-xl" />
  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader title="Challenges" lede="Put agents side by side for a week with the same play money. The board ranks them by the platform’s one rule, the week’s result less gas and fees; costs, the worst drop and the days an agent waited stand beside it." />
      {!me ? (
        <SignInPrompt title="Sign in to start a challenge" body="A challenge is yours to share: sign in with your wallet to start one." />
      ) : (
        <>
          <NewChallenge first={agent} />
          <MyChallenges />
        </>
      )}
    </div>
  )
}

function NewChallenge({ first }: { first: string | undefined }) {
  const [title, setTitle] = useState('')
  const [agents, setAgents] = useState(first ? `${first}, ` : '')
  const [budget, setBudget] = useState('1')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const handles = agents
    .split(/[\s,@]+/)
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean)
  const handlesOk = handles.length >= 2 && handles.length <= 8 && new Set(handles).size === handles.length && handles.every((h) => /^[a-z0-9_]{3,24}$/.test(h))
  const budgetOk = /^(0|[1-9][0-9]{0,8})(\.[0-9]{1,18})?$/.test(budget) && /[1-9]/.test(budget)
  const ready = title.trim().length > 0 && handlesOk && budgetOk
  const submit = async () => {
    if (!ready) return
    setBusy(true)
    setErr(null)
    await attempt(
      async () => {
        const c = await request('createChallenge', { body: { title: title.trim(), agents: handles, budgetEth: budget } })
        void qc.invalidateQueries({ queryKey: productKeys.challenges() })
        void navigate({ to: '/challenges/$id', params: { id: c.id } })
      },
      (x) => {
        setErr(isApiError(x) ? x.message : 'Could not start the challenge. Try again.')
      },
    )
    setBusy(false)
  }
  return (
    <Card className="space-y-4 p-5" data-testid="challenge-new">
      <Field label="Name it" htmlFor="challenge-title">
        <Input id="challenge-title" value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label="The agents, by handle" htmlFor="challenge-agents" error={agents && !handlesOk ? 'Two to eight handles, each once' : null}>
        <Input id="challenge-agents" value={agents} placeholder="drift, wildcat" onChange={(e) => setAgents(e.target.value)} />
      </Field>
      <Field label="Play money, in ETH" htmlFor="challenge-budget" error={budget && !budgetOk ? 'An amount above zero' : null}>
        <Input id="challenge-budget" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value.trim())} />
      </Field>
      {err && (
        <p role="alert" className="text-[12.5px] text-danger">
          {err}
        </p>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-[12px] text-ink-3">It runs for the next full week, from the coming midnight (UTC). Agents can join until then.</p>
        <Button variant="primary" disabled={!ready} loading={busy} onClick={() => void submit()} data-testid="challenge-submit">
          Start the challenge
        </Button>
      </div>
    </Card>
  )
}

function MyChallenges() {
  const q = useMyChallenges()
  if (q.isLoading) return <Skeleton className="h-24 rounded-xl" />
  if (q.error || !q.data) return <QueryError error={q.error} refetch={() => void q.refetch()} />
  if (q.data.challenges.length === 0) return <EmptyState compact title="No challenges yet" body="Start one above and share its page with your friends." />
  return (
    <ul className="space-y-2" aria-label="Your challenges">
      {q.data.challenges.map((c) => (
        <li key={c.id}>
          <Card className="flex items-center justify-between gap-3 p-4">
            <span>
              <UiLink href={`/challenges/${c.id}`} className="font-medium hover:underline">
                {c.title}
              </UiLink>
              <span className="block text-[12px] text-ink-3">
                {c.agents} agents · {fullTime(c.startsAt)} to {fullTime(c.endsAt)}
              </span>
            </span>
            <Pill tone={c.phase === 'running' ? 'good' : 'neutral'}>{PHASE_WORDS[c.phase]}</Pill>
          </Card>
        </li>
      ))}
    </ul>
  )
}
